import { useState } from "react";
import {
  Col,
  Row,
  Card,
  CardTitle,
  CardBody,
  Collapse,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage } from "react-intl";
import { FOCUS_CONTROL_MANUAL_PDF_PATH } from "../../constants/ConstantVars";
import { compareStrings } from "../../Utilities/sortHelper";

import {
  FaCheck,
  FaCircle,
  FaClipboard,
  FaClock,
  FaFileLines,
  FaPaperPlane,
  FaUsers,
  FaUser,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa6";

export default function FocusControl({
  targetSettings,
  deviantClasses,
  deviantStudents,
  onDelete,
  deletingTarget = null,
  inheritedSourceId,
  deviationSource,
  onSaveTarget,
  savingTarget = null,
}) {
  const focusLevels = [
    {
      value: 0,
      icon: FaCircle,
      color: "secondary",
      titleId: "FocusControlOff",
      descriptionId: "FocusControlOffDesc",
      helpId: "FocusControlHelp_Levels_Off",
    },
    {
      value: 1,
      icon: FaClipboard,
      color: "info",
      titleId: "FocusControlLog",
      descriptionId: "FocusControlLogDesc",
      helpId: "FocusControlHelp_Levels_Log",
    },
    {
      value: 2,
      icon: FaClock,
      color: "warning",
      titleId: "FocusControlDelay",
      descriptionId: "FocusControlDelayDesc",
      showDelayTiers: true,
      helpId: "FocusControlHelp_Levels_Delay",
    },
    {
      value: 3,
      icon: FaPaperPlane,
      color: "danger",
      titleId: "FocusControlSubmit",
      descriptionId: "FocusControlSubmitDesc",
      showMaxCount: true,
      helpId: "FocusControlHelp_Levels_Submit",
    },
  ];

  const [focusControlExpanded, setFocusControlExpanded] = useState(false);
  const [deviantExpanded, setDeviantExpanded] = useState(true);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const targets = [
    {
      id: "ModuleTest",
      targetIndex: 0,
      state: targetSettings?.moduleTest?.type ?? 0,
      isInherited: targetSettings?.moduleTest?.inherited ?? false,
      delaySeconds: targetSettings?.moduleTest?.delaySeconds ?? [],
      maxCount: targetSettings?.moduleTest?.maxCount,
    },
    {
      id: "FinalExam",
      targetIndex: 1,
      state: targetSettings?.finalExam?.type ?? 1,
      isInherited: targetSettings?.finalExam?.inherited ?? false,
      delaySeconds: targetSettings?.finalExam?.delaySeconds ?? [],
      maxCount: targetSettings?.finalExam?.maxCount,
    },
  ];

  const hasDeviantClasses = deviantClasses?.length > 0;
  const hasDeviantStudents = deviantStudents?.length > 0;
  const hasDeviantInfo = hasDeviantClasses || hasDeviantStudents;

  return (
    <Col lg={12}>
      <Card>
        <CardBody>
          <CardTitle
            onClick={() => setFocusControlExpanded(!focusControlExpanded)}
            style={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon
              style={{ marginRight: ".5em" }}
              className={
                focusControlExpanded
                  ? "fa-caret--animated collapsed"
                  : "fa-caret--animated"
              }
              icon={"caret-up"}
            />
            <FormattedMessage id="FocusControl" />
            <small className="ms-1 text-muted">
              (<FormattedMessage id="FocusControlGy25Only" />)
            </small>
            <FontAwesomeIcon
              icon="circle-info"
              className="ms-2 text-info"
              style={{ cursor: "pointer", fontSize: "0.9rem" }}
              onClick={(e) => {
                e.stopPropagation();
                setHelpModalOpen(true);
              }}
            />
          </CardTitle>
          <hr />
          <Collapse
            isOpen={focusControlExpanded}
            style={{ margin: "0 10px 0 10px" }}
          >
            <Row>
              {targets.map((target) => {
                const isLoading =
                  savingTarget === target.targetIndex ||
                  deletingTarget === target.targetIndex;
                return (
                  <Col md={6} key={target.id} className="mt-1 px-2 px-md-3">
                    <div className="p-3 rounded bg-light bg-opacity-15 border border-light">
                      <div
                        className="d-flex align-items-center mb-3"
                        style={{ minHeight: "36px" }}
                      >
                        <h5 className="mb-0">
                          <FormattedMessage id={target.id} />
                        </h5>
                        {target.isInherited && (
                          <Badge
                            color="info"
                            pill
                            className="ms-2 fw-normal"
                            style={{ fontSize: "0.7rem" }}
                          >
                            <FormattedMessage
                              id={inheritedSourceId || "InheritedSetting"}
                            />
                          </Badge>
                        )}
                        {onDelete && !target.isInherited && (
                          <>
                            <Badge
                              color="warning"
                              pill
                              className="ms-2 fw-normal"
                              style={{ fontSize: "0.7rem" }}
                            >
                              <FormattedMessage id="OwnSetting" />
                            </Badge>
                            <Button
                              size="sm"
                              color="info"
                              outline
                              className="ms-auto"
                              onClick={() => onDelete(target.targetIndex)}
                              disabled={isLoading}
                            >
                              <FormattedMessage id="InheritSetting" />
                            </Button>
                          </>
                        )}
                        {onDelete && target.isInherited && (
                          <span className="ms-auto" />
                        )}
                      </div>
                      <div
                        className="d-grid gap-1"
                        style={{ position: "relative" }}
                      >
                        {isLoading && (
                          <div className="d-flex align-items-center justify-content-center focus-control-loading-overlay">
                            <FontAwesomeIcon
                              icon="spinner"
                              spin
                              className="text-primary"
                              size="2x"
                            />
                          </div>
                        )}
                        {focusLevels.map((level) => {
                          const isSelected = target.state === level.value;
                          return (
                            <Card
                              key={level.value}
                              className={`mb-1 transition-all rounded ${
                                isSelected
                                  ? `border-${level.color} border-1 shadow-sm`
                                  : "border-light"
                              }`}
                              style={{
                                cursor: isLoading ? "default" : "pointer",
                                transition: "all 0.2s ease",
                                opacity: isLoading ? 0.6 : 1,
                                pointerEvents: isLoading ? "none" : "auto",
                              }}
                              onClick={() =>
                                target.state !== level.value &&
                                onSaveTarget?.(
                                  target.targetIndex,
                                  level.value,
                                  target.maxCount,
                                )
                              }
                            >
                              <CardBody className="p-3">
                                <div className="d-flex align-items-center">
                                  <div
                                    className={`me-3 d-flex align-items-center justify-content-center rounded-circle bg-${level.color} bg-opacity-10`}
                                    style={{
                                      width: "30px",
                                      height: "30px",
                                      minWidth: "30px",
                                    }}
                                  >
                                    <level.icon
                                      className={`text-${level.color}`}
                                    />
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div>
                                        <div className="fw-bold mb-1">
                                          <FormattedMessage
                                            id={level.titleId}
                                          />
                                        </div>
                                        <small className="text-muted">
                                          {level.showMaxCount ? (
                                            <div className="d-flex align-items-center gap-1">
                                              {isSelected ? (
                                                <UncontrolledDropdown
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <DropdownToggle
                                                    className="focus-control-max-count-toggle"
                                                    tag="span"
                                                  >
                                                    {target.maxCount}
                                                  </DropdownToggle>
                                                  <DropdownMenu
                                                    className="focus-control-max-count-menu"
                                                    container="body"
                                                  >
                                                    {Array.from(
                                                      { length: 10 },
                                                      (_, i) => i + 1,
                                                    ).map((n) => (
                                                      <DropdownItem
                                                        className="focus-control-max-count-item"
                                                        key={n}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (target.maxCount !== n) {
                                                            onSaveTarget?.(
                                                              target.targetIndex,
                                                              target.state,
                                                              n,
                                                            );
                                                          }
                                                        }}
                                                        active={
                                                          target.maxCount === n
                                                        }
                                                      >
                                                        {n}
                                                      </DropdownItem>
                                                    ))}
                                                  </DropdownMenu>
                                                </UncontrolledDropdown>
                                              ) : (
                                                <span className="focus-control-max-count-value">
                                                  {target.maxCount}
                                                </span>
                                              )}
                                              <span className="text-nowrap focus-control-max-count-label">
                                                <FormattedMessage
                                                  id="FocusControlMaxCount"
                                                  values={{
                                                    count: target.maxCount,
                                                  }}
                                                />
                                              </span>
                                            </div>
                                          ) : (
                                            <FormattedMessage
                                              id={level.descriptionId}
                                            />
                                          )}
                                          {level.showDelayTiers &&
                                            target.delaySeconds.length > 0 && (
                                              <div className="d-flex flex-wrap gap-1 mt-1">
                                                {target.delaySeconds.map(
                                                  (sec, i) => (
                                                    <span
                                                      key={i}
                                                      className="badge bg-warning bg-opacity-25 text-dark fw-normal"
                                                      style={{
                                                        fontSize: "0.65rem",
                                                      }}
                                                    >
                                                      {sec}s
                                                    </span>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                        </small>
                                      </div>
                                      {isSelected && (
                                        <FaCheck
                                          className={`text-${level.color} fa-lg`}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            {hasDeviantInfo && (
              <Row className="mt-3">
                <Col className="px-2 px-md-3">
                  <div className="p-3 rounded bg-light bg-opacity-15 border border-light">
                    <div
                      className="d-flex align-items-center justify-content-between"
                      style={{ cursor: "pointer" }}
                      onClick={() => setDeviantExpanded(!deviantExpanded)}
                    >
                      <span>
                        <FormattedMessage
                          id={`DeviatingSettings${deviationSource}`}
                        />
                      </span>
                      {deviantExpanded ? (
                        <FaChevronUp className="text-muted" />
                      ) : (
                        <FaChevronDown className="text-muted" />
                      )}
                    </div>

                    {deviantExpanded && (
                      <div className="mt-2 pt-2 border-top small">
                        <Row>
                          {hasDeviantClasses && (
                            <Col md={6}>
                              <div className="d-flex align-items-start mb-1">
                                <FaUsers className="text-primary me-1 mt-1" />
                                <strong>{deviantClasses.length}</strong>
                                <span className="ms-1">
                                  <FormattedMessage
                                    id={
                                      deviantClasses.length === 1
                                        ? "ClassDeviation"
                                        : "ClassesDeviation"
                                    }
                                  />
                                </span>
                              </div>
                              {deviantClasses.map((cls) => (
                                <div key={cls.className} className="ms-3">
                                  {cls.className}
                                </div>
                              ))}
                            </Col>
                          )}
                          {hasDeviantStudents && (
                            <Col md={6}>
                              <div className="d-flex align-items-start mb-1">
                                <FaUser className="text-primary me-1 mt-1" />
                                <strong>{deviantStudents.length}</strong>
                                <span className="ms-1">
                                  <FormattedMessage
                                    id={
                                      deviantStudents.length === 1
                                        ? "StudentDeviation"
                                        : "StudentsDeviation"
                                    }
                                  />
                                </span>
                              </div>
                              {deviantStudents
                                .filter((s) => s.studentName)
                                .sort((a, b) =>
                                  compareStrings(a.studentName, b.studentName),
                                )
                                .map((s) => (
                                  <div key={s.studentName} className="ms-3">
                                    {s.studentName} ({s.className})
                                  </div>
                                ))}
                            </Col>
                          )}
                        </Row>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </Collapse>
        </CardBody>
      </Card>

      <Modal
        isOpen={helpModalOpen}
        toggle={() => setHelpModalOpen(false)}
        size="md"
      >
        <ModalHeader toggle={() => setHelpModalOpen(false)}>
          <FormattedMessage id="FocusControlHelp_Title" />
        </ModalHeader>
        <ModalBody className="m-2">
          <div className="mb-3">
            <FormattedMessage id="FocusControlHelp_Intro" />
          </div>
          <h6 className="fw-bold mb-3">
            <FormattedMessage id="FocusControlHelp_Levels_Title" />
          </h6>
          <div className="d-grid gap-2 mb-3">
            {focusLevels.map((level) => (
              <div
                key={level.helpId}
                className="d-flex align-items-start p-2 rounded border border-light"
              >
                <div
                  className={`me-2 d-flex align-items-center justify-content-center rounded-circle bg-${level.color} bg-opacity-10 focus-control-help-icon`}
                >
                  <level.icon
                    className={`text-${level.color}`}
                    style={{ fontSize: "0.7rem" }}
                  />
                </div>
                <span>
                  <strong>
                    <FormattedMessage id={level.titleId} />
                  </strong>
                  {" – "}
                  <FormattedMessage id={level.helpId} />
                </span>
              </div>
            ))}
          </div>
          <hr className="my-3" />
          <p className="text-muted small mb-0">
            <FaUsers className="me-1 text-primary" />
            <FormattedMessage id="FocusControlHelp_Inheritance" />
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setHelpModalOpen(false)}
          >
            <FormattedMessage id="Close" />
          </Button>
          <Button
            color="primary"
            outline
            tag="a"
            href={FOCUS_CONTROL_MANUAL_PDF_PATH}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaFileLines className="me-1 mb-1" />
            <FormattedMessage id="FocusControlHelp_OpenManual" />
          </Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
}
